import React from "react";

import clsx from "clsx";

import Seat from "@busbud/design-system-components/dist/Seat";
import type { SeatCategory } from "@busbud/design-system-components/dist/Seat/Seat.types";
import type {
  SeatBaseState,
  Orientation
} from "@busbud/design-system-components/dist/SeatBase";
import type * as UDM from "@busbud/int-schemas/udm";

import { isSeatCell } from "@app/helpers/udm/vehicle";
import {
  PREMIUM,
  SALON_CAMA
} from "@app/modules/results/utils/classes/normalized-class-name";

import { useStyles } from "./cell.styles";

type Props = {
  cell?: UDM.Cell;
  orientation: Orientation;
  active_seat_id: string | null;
  already_assigned_seat_ids: (string | null)[];
  onSeatSelected: (seat: UDM.SeatCell) => void;
  seatClassResolver?: (seat_id: string) => string | null;
};

const Cell: React.FC<Props> = props => {
  const {
    cell,
    orientation,
    active_seat_id,
    already_assigned_seat_ids,
    onSeatSelected,
    seatClassResolver
  } = props;

  if (!cell || !isSeatCell(cell)) {
    return <EmptyCell />;
  }

  const seat_state = _getSeatState(
    cell,
    active_seat_id,
    already_assigned_seat_ids
  );

  let seat_category: SeatCategory = "standard";

  if (seatClassResolver) {
    const class_name = seatClassResolver(cell.id);
    if (class_name === SALON_CAMA) {
      seat_category = "recline";
    } else if (class_name === PREMIUM) {
      seat_category = "premium";
    }
  }

  let handleSelectSeat;
  if (
    (cell.availability && seat_state === "selected") ||
    (cell.availability && seat_state === "available")
  ) {
    handleSelectSeat = onSeatSelected.bind(this, cell);
  }

  return (
    /* eslint-disable-next-line
       jsx-a11y/no-static-element-interactions,
       jsx-a11y/click-events-have-key-events
       -- TODO: [fix-jsx-a11y] */
    <div
      onClick={handleSelectSeat}
      data-cy={`select-seat-${seat_state}-${cell.gender || "any"}`}
      data-testid="cell"
    >
      <Seat
        className={clsx({ "opacity-70": seat_state.startsWith("unavailable") })}
        seatNumber={cell.display_name}
        state={seat_state}
        orientation={orientation}
        category={seat_category}
      />
    </div>
  );
};

const EmptyCell = () => {
  const { classes } = useStyles();
  return <div className={classes.emptyCell} data-testid="empty-cell" />;
};

function _getSeatState(
  cell: UDM.SeatCell,
  active_seat_id: string | null,
  already_assigned_seat_ids: (string | null)[]
): SeatBaseState {
  const is_selected = cell.id === active_seat_id;

  const is_occupied_by_other_passenger =
    already_assigned_seat_ids && already_assigned_seat_ids.includes(cell.id);
  const { availability, gender } = cell;

  if (is_selected || is_occupied_by_other_passenger) {
    return "selected";
  }

  if (!availability) {
    return gender === "female"
      ? "unavailableWoman"
      : gender === "male"
        ? "unavailableMan"
        : "unavailable";
  }

  return "available";
}

export { Cell };
